import { Component, Prop, h } from '@stencil/core';

const COMPONENT_NAME = 'me-container';

type SpacingSize = '2xs' | 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl' | '4xl' | '5xl' | '6xl' | '7xl';

const DEFAULTS = {
  yGap: '5xl' as SpacingSize,
  yGapLg: '7xl' as SpacingSize,
} as const;

/**
 * A layout container component that provides optional vertical and horizontal spacing.
 *
 * Used as a wrapper for content within layout sections (e.g., inside `<me-section>`).
 * Supports different spacing sizes and flexible root tag rendering.
 */
@Component({
  tag: 'me-container',
  styleUrl: 'index.scss',
  shadow: false,
})
export class Container {
  /**
   * The HTML tag to use for the container element.
   * Defaults to `'div'`.
   */
  @Prop() tag: 'div' | 'section' = 'div';

  /**
   * Controls the amount of vertical spacing (padding) applied to the container.
   */
  @Prop() yGap: SpacingSize = DEFAULTS.yGap;
  @Prop() yGapLg: SpacingSize = DEFAULTS.yGapLg;

  /**
   * Controls the amount of horizontal spacing (padding) applied to the container.
   * When set, overrides the default horizontal padding from the base container styles.
   * xGap applies at all breakpoints, xGapMd at desktop (768px+), xGapLg at desktop-lg (1312px+).
   */
  @Prop() xGap: SpacingSize | undefined;
  @Prop() xGapMd: SpacingSize | undefined;
  @Prop() xGapLg: SpacingSize | undefined;

  render() {
    const Tag = this.tag;

    const classes = [
      COMPONENT_NAME,
      `${COMPONENT_NAME}--y-gap-${this.yGap}`,
      `${COMPONENT_NAME}--y-gap-lg-${this.yGapLg}`,
      this.xGap ? `${COMPONENT_NAME}--x-gap-${this.xGap}` : null,
      this.xGapMd ? `${COMPONENT_NAME}--x-gap-md-${this.xGapMd}` : null,
      this.xGapLg ? `${COMPONENT_NAME}--x-gap-lg-${this.xGapLg}` : null,
    ]
      .filter(Boolean)
      .join(' ');

    return (
      <Tag class={classes}>
        <slot></slot>
      </Tag>
    );
  }
}
